import React from "react";
import styles from "../styles/EdgeExplanationCard.module.css";

const pct = (value) =>
  typeof value === "number" && Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : "—";

export default function EdgeExplanationCard({
  edge,
  expectedGoals,
  notes = [],
  recommendation,
  formWindow = 5,
  showFormContext = true,
  cupContext = false,
  loading = false,
  error = "",
  onRetry,
  formatText = (value) => value,
  compact = false,
}) {
  const price = Number(edge?.price);
  const implied = Number.isFinite(price) && price > 1 ? 1 / price : null;
  const visibleNotes = compact ? notes.slice(0, 3) : notes;
  const hasExpectedGoals =
    expectedGoals &&
    typeof expectedGoals.home === "number" &&
    typeof expectedGoals.away === "number";

  return (
    <section className={`${styles.card} ${compact ? styles.compact : ""}`}>
      <div className={styles.header}>
        <div>
          <span className={styles.eyebrow}>Model read</span>
          <h4>Why this pick rates well</h4>
        </div>
        <div className={styles.badges}>
          {cupContext ? <span className={styles.cupBadge}>Cup context</span> : null}
          {showFormContext ? <span className={styles.sampleBadge}>Last {formWindow}</span> : null}
        </div>
      </div>

      {loading ? (
        <div className={styles.state}><span className={styles.loader} /> Building the match read…</div>
      ) : error ? (
        <div className={styles.state}>
          <span>{formatText(error)}</span>
          {onRetry ? <button onClick={onRetry}>Retry</button> : null}
        </div>
      ) : (
        <>
          <div className={styles.metrics}>
            <div className={styles.metric}>
              <span>Model chance</span>
              <strong>{pct(edge?.prob)}</strong>
            </div>
            <div className={styles.metric}>
              <span>Market chance</span>
              <strong>{pct(implied)}</strong>
            </div>
            <div className={`${styles.metric} ${styles.edgeMetric}`}>
              <span>Model edge</span>
              <strong>+{pct(edge?.edge)}</strong>
            </div>
            {hasExpectedGoals ? (
              <div className={styles.metric}>
                <span>Expected goals</span>
                <strong>{Number(expectedGoals.total ?? expectedGoals.home + expectedGoals.away).toFixed(2)}</strong>
                <small>{expectedGoals.home.toFixed(2)} + {expectedGoals.away.toFixed(2)}</small>
              </div>
            ) : null}
          </div>

          {visibleNotes.length ? (
            <div className={styles.evidence}>
              <span className={styles.sectionLabel}>Key evidence</span>
              <div className={styles.evidenceGrid}>
                {visibleNotes.map((note, index) => (
                  <div className={styles.evidenceItem} key={`${note}-${index}`}>
                    <span className={styles.check}>✓</span>
                    <span>{formatText(note)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {recommendation ? (
            <div className={styles.recommendation}>
              <div>
                <span>Value threshold</span>
                <strong>{formatText(recommendation)}</strong>
              </div>
              <span className={styles.signal}>Value signal</span>
            </div>
          ) : null}

          {!visibleNotes.length && !recommendation && !hasExpectedGoals ? (
            <div className={styles.state}>No detailed model evidence is available yet.</div>
          ) : null}
        </>
      )}
    </section>
  );
}
